import { Monitor, Moon, Sun } from "lucide-react";
import { cn } from "../../lib/cn";
import { useThemeStore, type ThemePreference } from "../../stores/theme-store";

const OPTIONS: Array<{
  value: ThemePreference;
  label: string;
  icon: typeof Sun;
}> = [
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
  { value: "system", label: "System", icon: Monitor },
];

/**
 * Three-option appearance control.
 *
 * A radiogroup rather than a switch: this is a choice between three mutually exclusive values,
 * and a switch only models two. Arrow-key navigation and the roving tabindex come from the
 * radiogroup semantics, so a keyboard user moves between options with arrows and lands on the
 * selected one when tabbing in — the behaviour a native radio set gives for free.
 *
 * Styled as a segmented control to match the Tabs component's visual language rather than
 * introducing a fourth kind of multi-option UI to the app.
 */
export function ThemeSwitcher({ className }: { className?: string }) {
  const preference = useThemeStore((s) => s.preference);
  const setPreference = useThemeStore((s) => s.setPreference);

  function onKeyDown(event: React.KeyboardEvent) {
    const delta = event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
    if (delta === 0) return;
    event.preventDefault();
    const index = OPTIONS.findIndex((o) => o.value === preference);
    const next = OPTIONS[(index + delta + OPTIONS.length) % OPTIONS.length];
    if (next) setPreference(next.value);
  }

  return (
    <div
      role="radiogroup"
      aria-label="Appearance"
      onKeyDown={onKeyDown}
      className={cn(
        "inline-flex gap-1 rounded-control border border-border bg-surface p-1",
        className,
      )}
    >
      {OPTIONS.map(({ value, label, icon: Icon }) => {
        const active = preference === value;
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={active}
            tabIndex={active ? 0 : -1}
            onClick={() => setPreference(value)}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-control px-3 py-1.5 text-sm font-medium",
              "transition-colors duration-fast ease-standard",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface",
              active
                ? "bg-accent-soft text-accent"
                : "text-text-muted hover:bg-surface-hover hover:text-text",
            )}
          >
            <Icon className="size-4" aria-hidden />
            {label}
          </button>
        );
      })}
    </div>
  );
}
